import { useNavigate } from '@tanstack/react-router';
import { Loader, MoreHorizontal } from 'lucide-react';
import { overlay } from 'overlay-kit';

import { QueryFilterEditModal } from '../../../entities/query-builder/query-filter-edit-modal';
import { useDeleteQuery } from '../../../entities/query/mutation/use-delete-query';

import { showDeleteQueryConfirm } from '../overlay/show-delete-query-confirm';

import { PLATFORM_LOGO_BY_TYPE } from '@/entities/public-filter/public-filter-constants';
import { useQueryProgress } from '@/entities/query/query/use-query-progress';
import type { SavedQuery } from '@/entities/query/query-types';
import { USER_ROLE } from '@/entities/user/user-constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/workspace-constants';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Card } from '@/shared/components/shadcn/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { useWorkspace } from '@/shared/hooks/use-workspace';

interface Props {
  savedQuery: SavedQuery;
}

export function SavedQueryCard({ savedQuery }: Props) {
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;

  const navigate = useNavigate();
  const deleteQuery = useDeleteQuery();
  const queryProgress = useQueryProgress(workspaceId, savedQuery.id, savedQuery.type === 'private');

  const handleCardClick = () => {
    navigate({
      to: '/workspace/$workspaceId/insight',
      params: { workspaceId },
      search: { q: savedQuery.id },
    });
  };

  const hasRunningQuery = queryProgress.some((item) => item.status === 'running');

  return (
    <Card className="group flex flex-row items-center justify-between p-0 transition-shadow hover:shadow-md">
      <div
        className="flex flex-1 cursor-pointer flex-col gap-4 px-6 py-6"
        onClick={handleCardClick}
      >
        <h3 className="flex gap-3">
          {savedQuery.type === 'public' && <Badge className="h-5">Sample</Badge>}
          <span className="text-base font-semibold">{savedQuery.name}</span>
        </h3>

        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2.5">
            {queryProgress.map((item) => {
              try {
                const PlatformLogo = PLATFORM_LOGO_BY_TYPE[item.platform];
                return (
                  <>
                    <PlatformLogo key={item.platform} className="size-4" />
                  </>
                );
              } catch (e) {
                console.error(e);
                return null;
              }
            })}
          </div>
          {hasRunningQuery && (
            <div className="flex items-center gap-2">
              <Loader className="text-muted-foreground size-4 animate-spin" />
              <span className="text-muted-foreground">Generating...</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 px-6 py-6">
        <Button
          variant="outline"
          disabled={
            savedQuery.type === 'public' ||
            queryProgress.some((item) => item.status === 'running') ||
            workspace?.privilege === USER_ROLE.VIEWER
          }
          onClick={() => {
            overlay.open(({ isOpen, close }) => (
              <QueryFilterEditModal
                isOpen={isOpen}
                onClose={close}
                unmount={close}
                savedQuery={savedQuery}
                workspaceId={workspaceId}
              />
            ));
          }}
        >
          Edit filter
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive"
              disabled={
                savedQuery.type === 'public' ||
                deleteQuery.isPending ||
                workspace?.privilege === USER_ROLE.VIEWER
              }
              onClick={async () => {
                const action = await showDeleteQueryConfirm(
                  savedQuery,
                  workspace?.plan === WORKSPACE_PLAN.TRIAL,
                );

                if (action === 'confirm') {
                  await deleteQuery.mutateAsync({
                    workspaceId,
                    queryId: savedQuery.id,
                  });
                }
              }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}
